<template>
  <div dir="ltr" class="reports-container">
    <div class="reports-header">
      <h1 class="reports-title">Call Reports</h1>
      <p class="reports-subtitle">View and download monthly call reports</p>
    </div>

    <!-- Year Selector -->
    <div class="year-selector">
      <div class="year-quick-buttons">
        <button class="year-quick-btn" :class="{ active: selectedYear === currentYear }"
          @click="selectYear(currentYear)">
          {{ currentYear }}
          <span class="current-badge">Current</span>
        </button>
        <button class="year-quick-btn" :class="{ active: selectedYear === currentYear - 1 }"
          @click="selectYear(currentYear - 1)">
          {{ currentYear - 1 }}
        </button>
      </div>
      <div class="year-dropdown-wrapper">
        <select v-model="selectedYear" @change="onYearChange" class="year-dropdown">
          <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
        </select>
        <svg class="dropdown-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="loader">
        <svg class="circular" viewBox="25 25 50 50">
          <circle class="path" cx="50" cy="50" r="20" fill="none" stroke-width="4" stroke-miterlimit="10" />
        </svg>
      </div>
      <p class="loading-text">Loading reports...</p>
    </div>

    <div v-else-if="reports.length === 0" class="empty-state">
      <div class="empty-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      </div>
      <p class="empty-text">No reports available at the moment</p>
    </div>

    <div v-else class="reports-grid">
      <div v-for="(report, index) in reports" :key="index" class="report-card"
        :class="{ 'downloading': downloadingIndex === index }">
        <div class="report-card-header">
          <div class="report-month">{{ report.month }}</div>
          <div class="report-badge">
            <span class="badge-dot"></span>
            <span class="badge-text">Call Report</span>
          </div>
        </div>

        <div class="report-stats">
          <div class="stat-item">
            <div class="stat-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ report.totalDuration }}</div>
              <div class="stat-label">Minutes</div>
            </div>
          </div>

          <div class="stat-divider"></div>

          <div class="stat-item">
            <div class="stat-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path
                  d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z">
                </path>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ report.entrance }}</div>
              <div class="stat-label">Entries</div>
            </div>
          </div>
        </div>

        <button class="download-button" @click="exportExcel(index)" :disabled="downloadingIndex === index">
          <span v-if="downloadingIndex === index" class="download-spinner"></span>
          <span v-else class="download-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </span>
          <span class="download-text">{{ downloadingIndex === index ? 'Downloading...' : 'Download Report' }}</span>
        </button>
      </div>
    </div>

    <transition name="fade">
      <div v-if="snackbar" class="snackbar" :class="snackbarColorBt">
        {{ snacbarText }}
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  data() {
    const currentYear = new Date().getFullYear();
    return {
      reports: [],
      loading: true,
      downloadingIndex: null,
      snackbar: false,
      snackbarColorBt: "success",
      snacbarText: "",
      currentYear,
      selectedYear: currentYear,
      abortController: null,
    };
  },
  computed: {
    yearOptions() {
      const years = [];
      for (let y = 2023; y <= 2053; y++) {
        years.push(y);
      }
      return years;
    }
  },
  methods: {
    async getReports() {
      try {
        this.loading = true;

        // ביטול הבקשה הקודמת אם עדיין רצה
        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        let token = localStorage.getItem("token");
        let api = process.env.VUE_APP_BASE_URL + `/reports/get_reports_details?year=${this.selectedYear}`;
        const res = await fetch(api, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: this.abortController.signal
        });

        if (res.status >= 400) {
          this.showSnackBar("Error receiving data", "error");
        } else if (res.status === 200) {
          const jsonObject = await res.json();
          this.reports = jsonObject;
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return; // ✅ בקשה בוטלה - לא עושים כלום
        }
        this.showSnackBar("Error loading reports: " + error, "error");
      } finally {
        this.loading = false;
      }
    },

    async exportExcel(index) {
      try {
        this.downloadingIndex = index;
        const selectedMonth = this.reports[index].month;
        const token = localStorage.getItem("token");

        const response = await fetch(`${process.env.VUE_APP_BASE_URL}/reports/export_report?date=${selectedMonth}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          // Updated filename to English
          link.download = `call_report_${selectedMonth.replace('/', '-')}.csv`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          this.showSnackBar(`Report for ${selectedMonth} downloaded successfully`, "success");
        } else {
          this.showSnackBar("Error downloading report", "error");
        }
      } catch (error) {
        this.showSnackBar("Error downloading report: " + error, "error");
      } finally {
        this.downloadingIndex = null;
      }
    },

    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true;

      setTimeout(() => {
        this.snackbar = false;
      }, 3000);
    },

    selectYear(year) {
      this.selectedYear = year;
      this.getReports();
    },

    onYearChange() {
      this.getReports();
    }
  },
  mounted() {
    this.getReports();
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
}
</script>

<style scoped>
/* Base styles */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Container */
.reports-container {
  font-family: 'Heebo', Arial, sans-serif;
  padding: 2.3rem;
  margin: 0 auto;
}

/* Header */
.reports-header {
  margin-bottom: 2rem;
  text-align: left;
  /* Changed from right to left */
}

.reports-title {
  font-size: 2rem;
  font-weight: 700;
  color: #0d2c6d;
  margin-bottom: 0.5rem;
}

.reports-subtitle {
  font-size: 1rem;
  color: #64748b;
}

/* Year Selector */
.year-selector {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.year-quick-buttons {
  display: flex;
  gap: 0.5rem;
}

.year-quick-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 1.2rem;
  border: 2px solid rgba(13, 44, 109, 0.15);
  border-radius: 10px;
  background-color: white;
  color: #0d2c6d;
  font-weight: 600;
  font-size: 0.95rem;
  font-family: 'Heebo', Arial, sans-serif;
  cursor: pointer;
  transition: all 0.25s ease;
}

.year-quick-btn:hover {
  border-color: #0d2c6d;
  background-color: rgba(13, 44, 109, 0.04);
}

.year-quick-btn.active {
  background-color: #0d2c6d;
  border-color: #0d2c6d;
  color: white;
}

.year-quick-btn.active .current-badge {
  background-color: #fdb71b;
  color: #0d2c6d;
}

.current-badge {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  background-color: rgba(13, 44, 109, 0.1);
  color: #0d2c6d;
}

.year-dropdown-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.year-dropdown {
  appearance: none;
  -webkit-appearance: none;
  padding: 0.6rem 2.2rem 0.6rem 1rem;
  border: 2px solid rgba(13, 44, 109, 0.15);
  border-radius: 10px;
  background-color: white;
  color: #0d2c6d;
  font-weight: 600;
  font-size: 0.95rem;
  font-family: 'Heebo', Arial, sans-serif;
  cursor: pointer;
  transition: all 0.25s ease;
}

.year-dropdown:hover {
  border-color: #0d2c6d;
}

.year-dropdown:focus {
  outline: none;
  border-color: #0d2c6d;
  box-shadow: 0 0 0 3px rgba(13, 44, 109, 0.1);
}

.dropdown-arrow {
  position: absolute;
  right: 0.7rem;
  color: #0d2c6d;
  pointer-events: none;
}

/* Grid layout */
.reports-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2.5rem;
}

/* Report card */
.report-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;
}

.report-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.report-card.downloading {
  opacity: 0.8;
}

/* Card header */
.report-card-header {
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.report-month {
  font-size: 1.8rem;
  font-weight: 700;
  color: #0d2c6d;
}

.report-badge {
  display: flex;
  align-items: center;
  background-color: rgba(13, 44, 109, 0.1);
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
}

.badge-dot {
  width: 8px;
  height: 8px;
  background-color: #0d2c6d;
  border-radius: 50%;
  margin-right: 0.5rem;
  /* Changed from margin-left to margin-right */
}

.badge-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: #0d2c6d;
}

/* Stats section */
.report-stats {
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-item {
  display: flex;
  align-items: center;
  flex: 1;
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: rgba(13, 44, 109, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  /* Changed from margin-left to margin-right */
  color: #0d2c6d;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0d2c6d;
}

.stat-label {
  font-size: 0.875rem;
  color: #64748b;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background-color: rgba(0, 0, 0, 0.1);
  margin: 0 1rem;
}

/* Download button */
.download-button {
  width: 100%;
  padding: 1rem;
  background-color: #fdb71b;
  border: none;
  color: #0d2c6d;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
}

.download-button:hover {
  background-color: #f5a700;
}

.download-button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.download-icon {
  margin-right: 0.5rem;
  /* Changed from margin-left to margin-right */
}

.download-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(13, 44, 109, 0.3);
  border-radius: 50%;
  border-top-color: #0d2c6d;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
  /* Changed from margin-left to margin-right */
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Loading state */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.loader {
  position: relative;
  width: 60px;
  height: 60px;
  margin-bottom: 1rem;
}

.circular {
  animation: rotate 2s linear infinite;
  height: 100%;
  transform-origin: center center;
  width: 100%;
}

.path {
  stroke: #0d2c6d;
  stroke-dasharray: 89, 200;
  stroke-dashoffset: 0;
  animation: dash 1.5s ease-in-out infinite;
  stroke-linecap: round;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }

  50% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -35px;
  }

  100% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -124px;
  }
}

.loading-text {
  font-size: 1rem;
  color: #64748b;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: #cbd5e1;
}

.empty-text {
  font-size: 1.125rem;
  color: #64748b;
}

/* Snackbar */
.snackbar {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
}

.snackbar.success {
  background-color: #10b981;
}

.snackbar.error {
  background-color: #ef4444;
}

.snackbar.warning {
  background-color: #f59e0b;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .reports-container {
    padding: 1.5rem;
  }

  .reports-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
}

@media (max-width: 480px) {
  .reports-container {
    padding: 1rem;
  }

  .reports-grid {
    grid-template-columns: 1fr;
  }

  .report-stats {
    flex-direction: column;
    align-items: flex-start;
  }

  .stat-item {
    margin-bottom: 1rem;
  }

  .stat-divider {
    display: none;
  }
}

/* Hebrew font */
@font-face {
  font-family: 'Heebo';
  font-style: normal;
  font-weight: 400;
  src: url(https://fonts.gstatic.com/s/heebo/v22/NGSpv5_NC0k9P_v6ZUCbLRAHxK1EiSysd0mm_00.woff2) format('woff2');
  unicode-range: U+0590-05FF, U+200C-2010, U+20AA, U+25CC, U+FB1D-FB4F;
}

@font-face {
  font-family: 'Heebo';
  font-style: normal;
  font-weight: 700;
  src: url(https://fonts.gstatic.com/s/heebo/v22/NGSpv5_NC0k9P_v6ZUCbLRAHxK1EiSysd0mm_00.woff2) format('woff2');
  unicode-range: U+0590-05FF, U+200C-2010, U+20AA, U+25CC, U+FB1D-FB4F;
}
</style>